import type { WorkspaceEntry } from '@crowdex/workspace-protocol';

/** A file of the conversation's repository the composer's @ can mention. */
export type RepositoryFile = {
  /** Relative to the repository's root. */
  path: string;
  name: string;
  /** Its folder, relative to the root ('' at the root). */
  directory: string;
  /** How Git sees it when it changed: modified, added, deleted, renamed, untracked. */
  status?: 'M' | 'A' | 'D' | 'R' | '?' | undefined;
};

export function repositoryFile(path: string, status?: RepositoryFile['status']): RepositoryFile {
  const slash = path.lastIndexOf('/');
  return {
    path,
    name: path.slice(slash + 1),
    directory: slash < 0 ? '' : path.slice(0, slash),
    ...(status === undefined ? {} : { status }),
  };
}

/** The changed files of `git status --short`, with their status. */
export function parseGitStatusFiles(status: string): RepositoryFile[] {
  return status.split('\n').flatMap((line) => {
    if (line.trim().length < 3) return [];
    const code = line.slice(0, 2);
    // A rename names the new path after the arrow.
    const path = line.slice(3).split(' -> ').at(-1)?.replace(/^"|"$/g, '') ?? '';
    if (path.length === 0 || path.endsWith('/')) return [];
    const status: RepositoryFile['status'] = code.includes('?')
      ? '?'
      : code.includes('A')
        ? 'A'
        : code.includes('D')
          ? 'D'
          : code.includes('R')
            ? 'R'
            : 'M';
    return [repositoryFile(path, status)];
  });
}

/** Folders a file search never walks into. */
const SKIPPED_DIRECTORIES = new Set([
  '.git',
  'node_modules',
  'dist',
  'build',
  'out',
  '.next',
  '.turbo',
  'coverage',
  'target',
  '.venv',
]);

/**
 * The names a repository's root `.gitignore` leaves out, the cheap part of it: plain names
 * (`dist`, `.env`, `build/`), not globs or paths, which only Git itself reads right.
 */
export function ignoredNames(gitignore: string): Set<string> {
  const names = new Set<string>();
  for (const raw of gitignore.split('\n')) {
    const line = raw.trim().replace(/^\//, '').replace(/\/$/, '');
    if (line.length === 0 || line.startsWith('#') || line.startsWith('!')) continue;
    if (/[*?[\]/]/.test(line)) continue;
    names.add(line);
  }
  return names;
}

/** How many folders are listed at once: quick without flooding the workspace server. */
const LISTING_CONCURRENCY = 6;

/**
 * The files of a repository, walked breadth first from its root through `listDirectory`, a few
 * folders at a time, at most `limit` files: enough to find one by name without reading a whole
 * monorepo. Dependencies, build output and what the root `.gitignore` names are skipped.
 */
export async function listRepositoryFiles(
  listDirectory: (path: string) => Promise<WorkspaceEntry[]>,
  {
    limit = 4000,
    maxDirectories = 400,
    ignored = new Set<string>(),
  }: { limit?: number; maxDirectories?: number; ignored?: ReadonlySet<string> } = {},
): Promise<RepositoryFile[]> {
  const files: RepositoryFile[] = [];
  let queue = [''];
  let walked = 0;
  while (queue.length > 0 && files.length < limit && walked < maxDirectories) {
    const batch = queue.slice(0, Math.min(LISTING_CONCURRENCY, maxDirectories - walked));
    queue = queue.slice(batch.length);
    walked += batch.length;
    const listings = await Promise.all(
      batch.map((directory) =>
        listDirectory(directory).then(
          (entries) => ({ directory, entries }),
          () => ({ directory, entries: [] as WorkspaceEntry[] }),
        ),
      ),
    );
    for (const { directory, entries } of listings)
      for (const entry of entries) {
        if (ignored.has(entry.name)) continue;
        const path = directory === '' ? entry.name : `${directory}/${entry.name}`;
        if (entry.isDirectory) {
          if (!SKIPPED_DIRECTORIES.has(entry.name)) queue.push(path);
        } else if (files.length < limit) files.push(repositoryFile(path));
      }
  }
  return files;
}

/**
 * The files @ offers for what is typed: without a query, the changed ones; with one, the files
 * whose name (then path) contains it, changed ones first, at most `limit`.
 */
export function matchRepositoryFiles(
  changed: readonly RepositoryFile[],
  all: readonly RepositoryFile[],
  query: string,
  limit = 8,
): RepositoryFile[] {
  const needle = query.trim().toLowerCase();
  const changedPaths = new Set(changed.map((file) => file.path));
  const candidates = [...changed, ...all.filter((file) => !changedPaths.has(file.path))];
  if (needle.length === 0) return changed.slice(0, limit);
  const byName = candidates.filter((file) => file.name.toLowerCase().includes(needle));
  const byPath = candidates.filter(
    (file) => !file.name.toLowerCase().includes(needle) && file.path.toLowerCase().includes(needle),
  );
  return [...byName, ...byPath].slice(0, limit);
}
